import { FormattedMessage, useIntl } from "react-intl";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { Modal } from "@/components/design/Modal";

// The blurred "what do you want to make" gate (grill-me session, 2026-09).
// Shared so both the SHARE landing (asks before navigating anywhere) and
// ShareCreatePage (asks as a fallback when /share/create is opened directly,
// e.g. a bookmark, without a `?type=` param) render the exact same popup.
// Modal is the app-wide dialog primitive (bg-ink/30 backdrop-blur-md) so the
// page behind stays visible through the blur, not a bespoke overlay.
//
// Die dritte Kachel, die Ausschreibung, gibt es nur mit dem Feature share.listings:
// ohne es antwortet genesis auf keine Ausschreibungs-Operation, und eine Kachel,
// die in eine gesperrte Seite führt, wäre ein Angebot, das keins ist.

export type ShareCreateChoice = "post" | "project" | "listing";

export function ShareCreateEntryChoice({
  onChoose,
  onCancel,
  canCreateListing = false,
}: {
  onChoose: (choice: ShareCreateChoice) => void;
  onCancel: () => void;
  canCreateListing?: boolean;
}) {
  const intl = useIntl();
  const canCreateProject = useHasFeature(FEATURE_CATALOG.shareProjects.key);
  return (
    <Modal
      open
      onClose={onCancel}
      dismiss="light"
      title={intl.formatMessage({ id: "share.create.entryChoice.eyebrow", defaultMessage: "SHARE · New" })}
      maxWidth={canCreateListing ? "52rem" : "36rem"}
    >
      <h2 className="mb-4 font-serif text-smycles-display-s leading-tight">
        <FormattedMessage id="share.create.entryChoice.title" defaultMessage="What do you want to make?" />
      </h2>
      <div className={`grid grid-cols-1 gap-3 ${canCreateProject && canCreateListing ? "@sm:grid-cols-3" : canCreateProject || canCreateListing ? "@sm:grid-cols-2" : ""}`}>
        <button
          id="share-create-choice-post"
          type="button"
          onClick={() => onChoose("post")}
          className="box-button flex h-full flex-col p-4 text-left transition hover:-translate-y-0.5"
        >
          <div className="smycles-field-value font-medium">
            <FormattedMessage id="share.create.entryChoice.postTitle" defaultMessage="Make a post" />
          </div>
          <div className="smycles-field-meta mt-1.5 text-ink">
            <FormattedMessage
              id="share.create.entryChoice.postDesc"
              defaultMessage="Just report on something, no team search needed."
            />
          </div>
        </button>
        {canCreateProject ? (
          <button
            id="share-create-choice-project"
            type="button"
            onClick={() => onChoose("project")}
            className="box-button flex h-full flex-col p-4 text-left transition hover:-translate-y-0.5"
          >
            <div className="smycles-field-value font-medium">
              <FormattedMessage id="share.create.entryChoice.projectTitle" defaultMessage="Create a project" />
            </div>
            <div className="smycles-field-meta mt-1.5 text-ink">
              <FormattedMessage
                id="share.create.entryChoice.projectDesc"
                defaultMessage="Find a team, run a timeline, document progress."
              />
            </div>
          </button>
        ) : null}
        {canCreateListing ? (
          <button
            id="share-create-choice-listing"
            type="button"
            onClick={() => onChoose("listing")}
            className="box-button flex h-full flex-col p-4 text-left transition hover:-translate-y-0.5"
          >
            <div className="smycles-field-value font-medium">
              <FormattedMessage id="share.create.entryChoice.listingTitle" defaultMessage="Create a listing" />
            </div>
            <div className="smycles-field-meta mt-1.5 text-ink">
              <FormattedMessage
                id="share.create.entryChoice.listingDesc"
                defaultMessage="Post a position, programme or call, collect applications."
              />
            </div>
          </button>
        ) : null}
      </div>
    </Modal>
  );
}
